import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import Dialog from "components/dialog";
import PatientFieldWithSearch from "components/patient-field-with-search";
import { toPhoneDigits } from "components/patient-search/new-patient-seed.utils";
import type { AttachCallRequest } from "services/dialstack";
import type { Patient } from "services/types/patients.type";

import { displayNumber } from "../call-display.util";

export const RELATIONSHIP_MAX_LENGTH = 100;

type FormValues = {
  patient: Pick<Patient, "id" | "phone" | "secondary_phone"> | undefined;
};

type AttachCallDialogProps = {
  remoteNumber: string;
  onClose: () => void;
  // Resolves null on success (the host closes the dialog) or the error response.
  onAttach: (
    request: Omit<AttachCallRequest, "callId">
  ) => Promise<{ error: unknown } | null>;
  // Root styles (the softphone lifts its dialogs over the panel).
  sx?: SxProps;
};

// Attach an unmatched call to an existing patient, optionally noting who was
// actually on the line ("caretaker", "called on behalf of") and remembering
// the number on the patient's record for future auto-matching.
export default function AttachCallDialog({
  remoteNumber,
  onClose,
  onAttach,
  sx
}: AttachCallDialogProps) {
  const methods = useForm<FormValues>({
    defaultValues: { patient: undefined }
  });
  const {
    control,
    formState: { isValid },
    watch
  } = methods;
  const patient = watch("patient");
  const [relationship, setRelationship] = useState("");
  const [remember, setRemember] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const digits = toPhoneDigits(remoteNumber);
  const hasRelationship = relationship.trim().length > 0;
  // Nothing to remember when the patient already has this number, or when the
  // spare slot is taken (the backend would refuse it — we don't offer it).
  const patientHasNumber =
    Boolean(patient) &&
    (toPhoneDigits(patient?.phone ?? "") === digits ||
      toPhoneDigits(patient?.secondary_phone ?? "") === digits);
  const secondarySlotTaken = Boolean(patient?.secondary_phone);
  const canRemember =
    Boolean(patient) &&
    digits.length === 10 &&
    !patientHasNumber &&
    !secondarySlotTaken;

  const handleSubmit = methods.handleSubmit(async ({ patient: selected }) => {
    if (!selected) return;
    setIsSubmitting(true);
    const error = await onAttach({
      patient_id: selected.id,
      relationship: relationship.trim(),
      remember_number: canRemember && !hasRelationship && remember
    });
    setIsSubmitting(false);
    if (!error) onClose();
  });

  return (
    <FormProvider {...methods}>
      <Dialog
        title="Attach call to a patient"
        subtitle={`Call with ${displayNumber(remoteNumber)}`}
        open
        onClose={onClose}
        maxWidth="sm"
        sx={sx}
        actions={
          <>
            <Button onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              disabled={!isValid || !patient}
              loading={isSubmitting}
              onClick={handleSubmit}
              data-testid="attach-call-submit"
            >
              Attach call
            </LoadingButton>
          </>
        }
      >
        <Stack spacing={2} sx={{ pb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            The recording, transcript and summary of this call are filed to the
            patient&apos;s chart.
          </Typography>
          <PatientFieldWithSearch control={control} required label="Patient" />
          <TextField
            label="Relationship to patient (optional)"
            placeholder="e.g. caretaker, called on behalf of"
            value={relationship}
            onChange={(event) => setRelationship(event.target.value)}
            inputProps={{
              maxLength: RELATIONSHIP_MAX_LENGTH,
              "data-testid": "attach-call-relationship"
            }}
            size="small"
            fullWidth
          />
          {canRemember && (
            <Stack spacing={0}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={remember && !hasRelationship}
                    onChange={(event) => setRemember(event.target.checked)}
                    disabled={hasRelationship}
                    inputProps={
                      { "data-testid": "attach-call-remember" } as never
                    }
                  />
                }
                label={`Remember ${displayNumber(remoteNumber)} as this patient's number`}
              />
              {!hasRelationship && (
                <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
                  Saved as their secondary phone: every later call from this
                  number will match this patient. Leave it off for a vendor or a
                  shared line.
                </FormHelperText>
              )}
              {hasRelationship && (
                <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
                  A caretaker&apos;s number is not the patient&apos;s — clear
                  the relationship to remember it.
                </FormHelperText>
              )}
            </Stack>
          )}
        </Stack>
      </Dialog>
    </FormProvider>
  );
}
